<template>
  <div class="item-history-container glass-theme fade-in-page">
    <!-- API Loading Overlay with Animation - Fixed height version -->
    <transition name="loading-overlay">
      <div v-if="apiLoading" class="loading-overlay">
        <div class="loading-card loading-card-fixed">
          <div class="loading-animation">
            <div class="loading-ring">
              <div class="ring"></div>
              <div class="ring"></div>
              <div class="ring"></div>
              <div class="loading-logo">
                <i class="fas fa-history"></i>
              </div>
            </div>
          </div>
          <div class="loading-text">
            <h3>{{ loadingMessage }}</h3>
            <div class="loading-dots">
              <span class="dot"></span>
              <span class="dot"></span>
              <span class="dot"></span>
            </div>
          </div>
          <div class="loading-progress">
            <div class="progress-bar" :style="{ width: loadingProgress + '%' }"></div>
          </div>
          <div class="loading-tips loading-tips-fixed">
            <p class="tip-text">{{ currentTip }}</p>
          </div>
        </div>
      </div>
    </transition>

    <b-card header="Item History Search" class="mb-4 glass-card">
      <b-form @submit.prevent="searchItemHistory">
        <b-row>
          <b-col md="2">
            <b-form-group label="Product SKU" label-for="productSku">
              <b-form-input
                id="productSku"
                v-model="searchParams.product_sku"
                placeholder="Enter product SKU"
                required
                class="glass-input"
              ></b-form-input>
            </b-form-group>
          </b-col>

          <b-col md="2">
            <b-form-group label="Date Range" label-for="dateRange">
              <date-picker
                v-model="searchParams.date_range"
                range
                format="MM-DD-YYYY"
                value-type="format"
                style="font-size: 14px; text-align: center; border: 1px solid rgba(128,128,128,0.3); width: 100%; border-radius: 10px;"
                placeholder="Select date range"
                class="glass-datepicker"
              ></date-picker>
            </b-form-group>
          </b-col>
        </b-row>

        <b-button type="submit" variant="primary" :disabled="loading || apiLoading" class="glass-btn">
          <b-spinner small v-if="loading"></b-spinner>
          <span v-else>🔍</span>
          Search History
        </b-button>
      </b-form>
    </b-card>

    <!-- Results -->
    <b-card v-if="searchPerformed" header="Item History Results" class="glass-card">
      <div v-if="itemHistory.length === 0" class="text-center p-4">
        <b-alert show variant="info" class="glass-alert">No history found for this product SKU.</b-alert>
      </div>

      <div v-else>
        <b-table
          :items="itemHistory"
          :fields="fields"
          striped
          responsive
          class="item-history-table glass-table"
          :per-page="perPage"
          :current-page="currentPage"
        >
          <template #cell(changed_at)="data">
            <span class="cell-date">{{ formatDateTime(data.value) }}</span>
          </template>

          <template #cell(location)="data">
            <span class="cell-location">{{ data.item.location_name }} | {{ data.item.sublocation_name }}</span>
          </template>

          <template #cell(state_name)="data">
            <b-badge :variant="getStateVariant(data.item.state)" class="state-badge">
              {{ data.value }}
            </b-badge>
          </template>

          <template #cell(changed_by)="data">
            <span class="cell-user">{{ data.value || 'System' }}</span>
          </template>

          <template #cell(invoice_id)="data">
            <span v-if="data.value" class="cell-invoice">{{ data.value }}</span>
            <span v-else class="text-muted">—</span>
          </template>

          <template #cell(notes)="data">
            <span class="cell-notes">{{ transformNotes(data.item.notes) }}</span>
          </template>
        </b-table>

        <b-pagination
          v-model="currentPage"
          :total-rows="itemHistory.length"
          :per-page="perPage"
          align="center"
          class="my-4 glass-pagination"
        ></b-pagination>

        <div class="text-muted total-records">
          Total Records: {{ itemHistory.length }}
        </div>
      </div>
    </b-card>
  </div>
</template>

<script>
import Requestor from '@/utilities/Requestor';
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';

export default {
  name: "AdminToolsItemHistory",
  components: { DatePicker },
  data() {
    return {
      searchParams: {
        product_sku: '',
        date_range: []
      },
      itemHistory: [],
      loading: false,
      searchPerformed: false,
      perPage: 10,
      currentPage: 1,
      fields: [
        { key: 'changed_at', label: 'Date/Time', sortable: true },
        { key: 'state_name', label: 'Current State', sortable: true },
        { key: 'location', label: 'Current Location' },
        { key: 'changed_by', label: 'Changed By', sortable: true },
        { key: 'invoice_id', label: 'Invoice ID' },
        { key: 'notes', label: 'Notes' }
      ],
      // Loading animation properties
      apiLoading: false,
      loadingMessage: 'Searching History',
      loadingProgress: 0,
      currentTipIndex: 0,
      loadingInterval: null,
      tipInterval: null,
      msgInterval: null,
      apiCallStack: 0,
      tips: [
        "🔍 Searching item history...",
        "📋 Fetching records...",
        "📊 Processing data...",
        "🔄 Loading timeline...",
        "✨ Organizing results...",
        "📝 Preparing history report..."
      ]
    }
  },
  computed: {
    currentTip() {
      return this.tips[this.currentTipIndex] || this.tips[0];
    }
  },
  methods: {
    // Loading animation methods
    startApiLoading(message = 'Searching History') {
      this.apiCallStack++;
      if (!this.apiLoading) {
        this.startLoadingAnimation(message);
      } else {
        this.loadingMessage = message;
      }
    },
    stopApiLoading() {
      this.apiCallStack--;
      if (this.apiCallStack <= 0 && this.apiLoading) {
        this.stopLoadingAnimation(true);
      }
    },
    startLoadingAnimation(message = 'Searching History') {
      this.apiLoading = true;
      this.loadingMessage = message;
      this.loadingProgress = 0;
      this.currentTipIndex = 0;
      
      this.loadingInterval = setInterval(() => {
        if (this.loadingProgress < 90) {
          this.loadingProgress += Math.random() * 10;
          if (this.loadingProgress > 90) this.loadingProgress = 90;
        }
      }, 300);
      
      this.tipInterval = setInterval(() => {
        if (this.currentTipIndex < this.tips.length - 1) {
          this.currentTipIndex++;
        }
      }, 2000);
      
      let messages = ['Searching History', 'Fetching Data', 'Processing Records', 'Loading Timeline', 'Finalizing'];
      let msgIndex = 0;
      this.msgInterval = setInterval(() => {
        if (msgIndex < messages.length - 1) {
          msgIndex++;
          this.loadingMessage = messages[msgIndex];
        }
      }, 1800);
    },
    stopLoadingAnimation(success = true) {
      if (this.loadingInterval) clearInterval(this.loadingInterval);
      if (this.tipInterval) clearInterval(this.tipInterval);
      if (this.msgInterval) clearInterval(this.msgInterval);
      
      if (success) {
        this.loadingProgress = 100;
        setTimeout(() => {
          this.apiLoading = false;
        }, 500);
      } else {
        this.apiLoading = false;
      }
    },
    
    searchItemHistory() {
      let that = this;

      if (!this.searchParams.product_sku || this.searchParams.product_sku.trim() === '') {
        this.$bvToast.toast(
          "Please enter a product SKU.",
          { title: "Error", autoHideDelay: 1500, variant: "warning" }
        );
        return;
      }

      this.startApiLoading('Searching Item History');
      
      let payload = {
        product_sku: this.searchParams.product_sku,
        date_from: (this.searchParams.date_range && this.searchParams.date_range.length > 0) 
            ? this.searchParams.date_range[0] 
            : '',
        date_to: (this.searchParams.date_range && this.searchParams.date_range.length > 1) 
            ? this.searchParams.date_range[1] 
            : ''
      };

      let path = "/api/sales/item_search_history/";

      let s = function(response) {
        if (response.data.result) {
          that.itemHistory = response.data.result;
          that.searchPerformed = true;
          if (that.itemHistory.length === 0) {
            that.$bvToast.toast(
              "No history found for this product SKU.",
              { title: "Info", autoHideDelay: 1500, variant: "info" }
            );
          }
        } else {
          that.$bvToast.toast(
            response.data.error || "No history found.",
            { title: "Error", autoHideDelay: 1500, variant: "warning" }
          );
        }
        that.loading = false;
        that.stopApiLoading();
      };

      let f = function(error) {
        console.error("Error searching item history:", error);
        that.$bvToast.toast(
          "Error searching item history.",
          { title: "Error", autoHideDelay: 1500, variant: "danger" }
        );
        that.loading = false;
        that.stopApiLoading();
      };

      this.loading = true;
      this.itemHistory = [];
      Requestor.getSyncP(path, payload, this, s, f);
    },

    formatDateTime(dateTime) {
      if (!dateTime) return 'N/A';
      const date = new Date(dateTime);
      return date.toLocaleString();
    },

    getStateVariant(state) {
      const variants = {
        1: 'success',
        2: 'info',
        3: 'primary',
        4: 'warning',
        5: 'secondary',
        12: 'dark',
        30: 'danger',
        '-2': 'warning',
        '-4': 'danger',
        '-7': 'danger',
        '-8': 'warning'
      };
      return variants[state] || 'light';
    },

    getStateName(state) {
      const names = {
        1: 'DELIVERED',
        2: 'PULLED',
        3: 'PICKED',
        4: 'RECEIVED',
        5: 'READY_TO_SHIP',
        12: 'SHIPPED_FOR_DELIVERY',
        30: 'DROPPED',
        '-2': 'CANT PULL',
        '-4': 'CANT_RECEIVE',
        '-7': 'CANT_DELIVER',
        '-8': 'ATTENTION_NEEDED'
      };
      return names[state] || state;
    },

    transformNotes(note) {
      if (!note) return '';
  
      return note.replace(/Changed from (\-?\d+) to (\-?\d+)/, (match, from, to) => {
        return `Changed from ${this.getStateName(parseInt(from))} to ${this.getStateName(parseInt(to))}`;
      });
    }
  },
  beforeDestroy() {
    this.stopLoadingAnimation();
  }
}
</script>

<style scoped>
/* Glass Theme Background */
.glass-theme {
  position: relative;
  background: transparent !important;
  min-height: 100vh;
}

.glass-theme::before {
  content: '';
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: url('https://m.media-amazon.com/images/I/81eSegKonhL._AC_SL1500_.jpg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  filter: blur(10px);
  opacity: 0.5;
  z-index: -2;
  transform: scale(1.1);
}

/* Page Entrance Animation */
@keyframes fadeInPage {
  from {
    opacity: 0;
    transform: translateY(24px);
    filter: blur(2px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
    filter: blur(0);
  }
}

.fade-in-page {
  animation: fadeInPage 0.7s cubic-bezier(0.2, 0.9, 0.4, 1.1);
}

/* Glass Card */
.glass-card {
  background: rgba(255, 255, 255, 0.85) !important;
  backdrop-filter: blur(10px) !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
  border-radius: 20px !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05) !important;
  overflow: hidden;
}

.glass-card >>> .card-header {
  background: rgba(0, 0, 0, 0.05) !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.3) !important;
  font-weight: 600;
  color: #1e293b;
}

/* Glass Alert */
.glass-alert {
  background: rgba(255, 255, 255, 0.85) !important;
  backdrop-filter: blur(10px) !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
  border-radius: 12px !important;
}

/* Glass Button */
.glass-btn {
  background: linear-gradient(135deg, rgba(54, 64, 80, 0.9), rgba(54, 64, 80, 0.95)) !important;
  color: white !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  transition: all 0.2s ease !important;
  border-radius: 10px !important;
  padding: 8px 24px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.glass-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, rgba(54, 64, 80, 1), rgba(54, 64, 80, 1)) !important;
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2) !important;
}

.glass-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Glass Input */
.glass-input {
  background: rgba(255, 255, 255, 0.9) !important;
  backdrop-filter: blur(5px) !important;
  border: 1px solid rgba(128, 128, 128, 0.3) !important;
  border-radius: 10px !important;
}

.glass-input:focus {
  border-color: rgba(54, 64, 80, 0.5) !important;
  box-shadow: 0 0 0 2px rgba(54, 64, 80, 0.1) !important;
}

/* Glass Datepicker */
.glass-datepicker >>> .mx-input {
  background: rgba(255, 255, 255, 0.9) !important;
  border: 1px solid rgba(128, 128, 128, 0.3) !important;
  border-radius: 10px !important;
  padding: 8px 12px;
}

.glass-datepicker >>> .mx-input-wrapper {
  width: 100%;
}

/* Glass Table */
.glass-table {
  background: rgba(255, 255, 255, 0.7) !important;
  border-radius: 12px !important;
  overflow: hidden;
}

.glass-table >>> table {
  background: transparent !important;
}

.glass-table >>> th {
  background: rgba(0, 0, 0, 0.05) !important;
  color: #1e293b !important;
  font-weight: 600 !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1) !important;
}

.glass-table >>> td {
  background: rgba(255, 255, 255, 0.5) !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05) !important;
}

.glass-table >>> tr:hover td {
  background: rgba(255, 255, 255, 0.8) !important;
}

/* Glass Pagination */
.glass-pagination >>> .page-item .page-link {
  background: rgba(255, 255, 255, 0.8) !important;
  border: 1px solid rgba(0, 0, 0, 0.1) !important;
  color: #1e293b !important;
  border-radius: 8px !important;
  margin: 0 3px !important;
}

.glass-pagination >>> .page-item.active .page-link {
  background: linear-gradient(135deg, #1e293b, #0f172a) !important;
  color: white !important;
  border-color: transparent !important;
}

.glass-pagination >>> .page-item .page-link:hover {
  background: rgba(0, 0, 0, 0.1) !important;
  transform: translateY(-1px);
}

/* Cell Styles */
.cell-date {
  font-size: 0.85rem;
  color: #475569;
}

.cell-location {
  font-size: 0.85rem;
  color: #334155;
}

.cell-user {
  font-size: 0.85rem;
  color: #475569;
  font-weight: 500;
}

.cell-invoice {
  font-size: 0.85rem;
  color: #2563eb;
  font-weight: 500;
}

.cell-notes {
  font-size: 0.85rem;
  color: #64748b;
  max-width: 250px;
  display: inline-block;
  white-space: normal;
  word-break: break-word;
}

.state-badge {
  font-size: 0.75rem;
  padding: 4px 8px;
  border-radius: 20px;
}

.total-records {
  text-align: right;
  margin-top: 10px;
  font-size: 0.85rem;
}

/* Container */
.item-history-container {
  padding: 20px;
}

/* Loading Overlay Styles - FIXED HEIGHT VERSION */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(20px);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.loading-overlay-enter-active {
  animation: fadeIn 0.3s ease;
}

.loading-overlay-leave-active {
  animation: fadeOut 0.3s ease;
}

.loading-card {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(245, 245, 245, 0.95));
  border-radius: 24px;
  text-align: center;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
  animation: cardPop 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55);
  width: 400px;
  min-height: 420px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

@keyframes cardPop {
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.loading-animation {
  margin-bottom: 24px;
  flex-shrink: 0;
}

.loading-ring {
  position: relative;
  width: 80px;
  height: 80px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ring {
  position: absolute;
  width: 100%;
  height: 100%;
  border: 3px solid transparent;
  border-radius: 50%;
  animation: ringRotate 1.5s linear infinite;
}

.ring:nth-child(1) {
  border-top-color: #4299e1;
  border-left-color: #4299e1;
  animation-duration: 1s;
}

.ring:nth-child(2) {
  border-right-color: #48bb78;
  border-bottom-color: #48bb78;
  width: 80%;
  height: 80%;
  top: 10%;
  left: 10%;
  animation-duration: 1.2s;
  animation-direction: reverse;
}

.ring:nth-child(3) {
  border-left-color: #ed8936;
  border-bottom-color: #ed8936;
  width: 60%;
  height: 60%;
  top: 20%;
  left: 20%;
  animation-duration: 0.8s;
}

@keyframes ringRotate {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-logo {
  font-size: 28px;
  font-weight: bold;
  animation: logoPulse 1.5s ease-in-out infinite;
  z-index: 1;
}

.loading-logo i {
  font-size: 32px;
  background: linear-gradient(135deg, #4299e1, #48bb78);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

@keyframes logoPulse {
  0%, 100% { transform: scale(1); opacity: 0.7; }
  50% { transform: scale(1.1); opacity: 1; }
}

.loading-text {
  margin-bottom: 20px;
  flex-shrink: 0;
}

.loading-text h3 {
  color: #2d3748;
  font-size: 1.2rem;
  margin-bottom: 6px;
  display: inline-block;
}

.loading-dots {
  display: inline-flex;
  margin-left: 8px;
}

.loading-dots .dot {
  width: 5px;
  height: 5px;
  background: #4299e1;
  border-radius: 50%;
  margin: 0 2px;
  animation: dotBounce 1.4s ease-in-out infinite;
}

.loading-dots .dot:nth-child(1) { animation-delay: 0s; }
.loading-dots .dot:nth-child(2) { animation-delay: 0.2s; }
.loading-dots .dot:nth-child(3) { animation-delay: 0.4s; }

@keyframes dotBounce {
  0%, 60%, 100% { transform: translateY(0); }
  30% { transform: translateY(-6px); }
}

.loading-progress {
  width: 80%;
  height: 3px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
  margin: 0 auto 20px auto;
  flex-shrink: 0;
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #4299e1, #48bb78, #ed8936, #4299e1);
  background-size: 200% 100%;
  animation: progressShine 1.5s linear infinite;
  transition: width 0.3s ease;
  border-radius: 4px;
}

@keyframes progressShine {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.loading-tips {
  margin-top: 10px;
  flex-shrink: 0;
  min-height: 65px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.tip-text {
  color: #718096;
  font-size: 0.85rem;
  animation: tipFade 0.5s ease;
  margin: 0;
  padding: 0 15px;
  line-height: 1.4;
}

@keyframes tipFade {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes fadeOut {
  from { opacity: 1; }
  to { opacity: 0; }
}

/* Responsive */
@media (max-width: 768px) {
  .item-history-container {
    padding: 12px;
  }
  
  .glass-btn {
    width: 100%;
    justify-content: center;
  }
  
  .loading-card {
    width: 320px;
    min-height: 380px;
    padding: 30px 20px;
  }
  
  .loading-tips {
    min-height: 55px;
  }
  
  .tip-text {
    font-size: 0.75rem;
  }
  
  .glass-table {
    font-size: 0.75rem;
  }
  
  .cell-notes {
    max-width: 150px;
  }
}
</style>


// WEBPACK FOOTER //
// src/components/AdminTools/AdminToolsItemHistory.vue